import React from 'react'

import { get, has, find as lodashFind, uniqBy } from 'lodash'
import moment from 'moment'
import { handleActions } from 'redux-actions'
import { createSelector } from 'reselect'

import httpClient, { catchError } from '@emmacare/common/api/httpClient'
import { PROGRAMS } from '@emmacare/common/constants/programs'
import { hasProgramType } from '@emmacare/common/utils/programTypes'

import * as patientModule from 'src/store/modules/patient'
import {
  deletePatientData,
  setActivePatientEnrolledProgram,
  setIsDashboard,
  setNextCarePlanReviewDate,
  setPatientLastTelemedicineCall,
  setPatientLatestRisk,
  setPatientProfile,
  setPatientProfileIsLoading,
  setPatientReadingAnswers,
  setPatientRiskRecalculated,
  setPatientTotalChatMessages
} from 'src/store/modules/actionCreators'
import { setPhones } from 'src/store/modules/patient/demographics'
import {
  patientProfileSelector,
  patientProgramSelector
} from 'src/store/modules/patient/selectors'
import { setRecentlyViewedPatients } from 'src/store/modules/patients'

import {
  DATE_FORMAT_FOR_SERVER,
  DATE_TIME_FORMAT_FOR_SERVER,
  DETAILED_QUERY_PARAM,
  PATIENT,
  REMOVE_DEVICE_ID,
  UNLOCK_PATIENT,
  UNLOCK_PATIENT_MESSAGE
} from 'src/constants'
import api from 'src/services/@core/API'
import { showSuccessModal } from 'src/services/dialogs'

/* --------- reducer --------- */
const initialState = {
  activeEnrolledProgram: {},
  addresses: [],
  caseManager: {},
  companyDivision: {},
  dob: '',
  document: null,
  diseases: [],
  enabled: null,
  firstName: '',
  fullName: '',
  gender: {
    uuid: '',
    refValue: ''
  },
  group: {},
  languages: [],
  lastName: '',
  login: null,
  middleName: '',
  patientId: 0,
  phone: null,
  photo: [],
  enrolledProgramStatus: null,
  uuid: '',
  chatMessages: '0',
  readingAnswers: {},
  consentDate: null,
  isDashboard: false,
  gamificationEnabled: false,
  nextCarePlanReviewDate: null,
  patientProfileIsLoading: false,
  lastTelemedicineCall: null,
  riskRecalculatedTimestamp: null
}

export default handleActions(
  {
    [setPatientProfile](state, { payload: profile }) {
      return {
        ...state,
        ...profile
      }
    },

    [setPatientProfileIsLoading](state, { payload }) {
      return {
        ...state,
        patientProfileIsLoading: payload
      }
    },

    [setActivePatientEnrolledProgram](state, { payload }) {
      if (!payload) return state

      return {
        ...state,
        enrolledProgramStatus: payload.status
      }
    },

    [setPatientTotalChatMessages](state, { payload: chatMessages }) {
      return {
        ...state,
        chatMessages
      }
    },

    [setPatientReadingAnswers](state, { payload: readingAnswers }) {
      return {
        ...state,
        readingAnswers
      }
    },

    [setIsDashboard](state, { payload: isDashboard }) {
      return {
        ...state,
        isDashboard
      }
    },

    [setNextCarePlanReviewDate](state, { payload: nextCarePlanReviewDate }) {
      return {
        ...state,
        nextCarePlanReviewDate
      }
    },

    [setPatientLastTelemedicineCall](state, { payload: lastTelemedicineCall }) {
      return {
        ...state,
        lastTelemedicineCall
      }
    },

    [setPatientLatestRisk](state, { payload: latestRisk }) {
      return {
        ...state,
        latestRisk
      }
    },

    [setPatientRiskRecalculated](state) {
      return {
        ...state,
        riskRecalculatedTimestamp: Date.now()
      }
    },

    [deletePatientData]() {
      return initialState
    }
  },
  initialState
)

/* --------- helpers --------- */
const getRecentlyViewedPatients = (patient) => {
  try {
    const recentlyViewed = JSON.parse(
      localStorage.getItem('recentlyViewedPatients')
    )

    return uniqBy(
      [{ ...patient }].concat(recentlyViewed),
      (item) => item.uuid
    ).slice(0, 3)
  } catch (err) {
    return []
  }
}

/* --------- thunks --------- */
export const getPatientProfile = (id) => (dispatch) => {
  if (!id) {
    return Promise.reject(new Error('Id not available'))
  }

  dispatch(setPatientProfileIsLoading(true))

  return api
    .getPatientProfile({ id }, DETAILED_QUERY_PARAM)
    .then((profile) => {
      const { phones, ...restProfile } = profile
      const resentlyViewed = getRecentlyViewedPatients(profile)

      dispatch(setPatientProfile(restProfile))
      dispatch(setRecentlyViewedPatients(resentlyViewed))
      dispatch(setPhones(phones))
      dispatch(setPatientProfileIsLoading(false))

      localStorage.setItem(
        'recentlyViewedPatients',
        JSON.stringify(resentlyViewed)
      )

      return Promise.all([
        restProfile,
        dispatch(patientModule.getActiveProgram())
      ])
    })
    .catch((err) => Promise.reject(new Error(`Server error: ${err.message}`)))
    .finally(() => {
      dispatch(setPatientProfileIsLoading(false))
    })
}

export const fetchPatientProfile = () => async (dispatch, getState) => {
  const patientUuid = patientUuidSelector(getState())

  try {
    const { phones, ...restProfile } = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT,
        params: {
          id: patientUuid
        },
        query: DETAILED_QUERY_PARAM
      }
    })

    dispatch(setPatientProfile(restProfile))
    dispatch(setPhones(phones))
  } catch (err) {
    catchError(err)
  }
}

export const refreshPatientLatestRisk = (patientUuid) => async (dispatch) => {
  try {
    const profile = await httpClient.request({
      method: 'get',
      url: {
        pathname: PATIENT,
        params: {
          id: patientUuid
        },
        query: DETAILED_QUERY_PARAM
      }
    })

    // Only update latestRisk field to avoid breaking other UI components
    dispatch(setPatientLatestRisk(profile.latestRisk))
  } catch (err) {
    catchError(err)
  }
}

export const getPatientChatTotalMessages = (config) => async (dispatch) => {
  try {
    const messages = await api.getPatientChatTotalMessages(config)
    dispatch(setPatientTotalChatMessages(messages))
  } catch (err) {
    catchError(err)
    dispatch(setPatientTotalChatMessages('0'))
  }
}

export const changePatient =
  (id, payload, shouldRemoveDeviceId = false) =>
  async (dispatch) => {
    if (!id || !payload) {
      const errorReason = !id ? 'User id' : 'Data'
      return Promise.reject(new Error(`${errorReason} not available`))
    }

    if (has(payload, 'shippingDate') && moment.isMoment(payload.shippingDate)) {
      payload.shippingDate = payload.shippingDate.format(DATE_FORMAT_FOR_SERVER)
    }

    if (has(payload, 'trainingDate') && moment.isMoment(payload.trainingDate)) {
      payload.trainingDate = payload.trainingDate.format(
        DATE_TIME_FORMAT_FOR_SERVER
      )
    }

    try {
      const data = await api.patchPatientProfile(
        { id },
        DETAILED_QUERY_PARAM,
        payload
      )

      if (shouldRemoveDeviceId) {
        dispatch(removeDeviceId(id, false, false))
      }

      dispatch(setPatientProfile(data))
    } catch (err) {
      if (err.errors) {
        const message = Object.keys(get(err, 'errors', [])).map((key) => (
          <div key={key}>
            <span
              style={{
                fontWeight: 'bold',
                textTransform: 'capitalize'
              }}
            >{`${key}: `}</span>
            <span>{`${err.errors[key][0]}\n`}</span>
          </div>
        ))

        const error = {
          status: err.status,
          message
        }
        catchError(error, { title: err.message })
      } else {
        catchError(err)
      }
    }
  }

export const updatePatient =
  (config = {}) =>
  async (dispatch) => {
    const { params = {}, payload = {}, query = {} } = config

    try {
      const patientProfile = await httpClient.request({
        method: 'patch',
        url: {
          pathname: PATIENT,
          params,
          query
        },
        payload
      })

      dispatch(setPatientProfile(patientProfile))
    } catch (err) {
      catchError(err)
    }
  }

export const sendSmsAppLink = (params) => async () => {
  try {
    await api.sendSmsAppLink({ params })
    showSuccessModal()
  } catch (err) {
    if (!err.message) err.message = 'Enrolled program is not set!'
    catchError(err)
  }
}

export const getPatientReadingAnswers = (config) => async (dispatch) => {
  try {
    const { items } = await api.getPatientReadingAnswers(config)
    dispatch(setPatientReadingAnswers(items))
  } catch (err) {
    catchError(err)
  }
}

export const cleanProfile = () => (dispatch) => {
  dispatch(deletePatientData())
}

export const removeDeviceId =
  (
    patientUuid,
    shouldUpdateProfile = true,
    showSuccessModalOnComplete = true
  ) =>
  async (dispatch) => {
    try {
      await httpClient.request({
        method: 'delete',
        url: {
          pathname: REMOVE_DEVICE_ID
        },
        payload: { patient_uuid: patientUuid }
      })
      if (showSuccessModalOnComplete) {
        showSuccessModal()
      }
      if (shouldUpdateProfile) {
        dispatch(getPatientProfile(patientUuid))
      }
    } catch (err) {
      catchError(err)
    }
  }

export const unlockPatient = (uuid) => async (dispatch) => {
  try {
    await httpClient.request({
      method: 'post',
      url: {
        pathname: UNLOCK_PATIENT,
        params: { uuid }
      }
    })
    showSuccessModal({ content: UNLOCK_PATIENT_MESSAGE })
    dispatch(getPatientProfile(uuid))
  } catch (err) {
    catchError(err)
  }
}

/* --------- selectors --------- */
export const patientIdSelector = createSelector(
  patientProfileSelector,
  (profile) => profile.patientId
)

export const patientProfileIsLoadingSelector = createSelector(
  patientProfileSelector,
  (profile) => profile.patientProfileIsLoading
)

export const patientUuidSelector = createSelector(
  patientProfileSelector,
  (profile) => profile.uuid
)

export const patientHasReferralSourceSelector = createSelector(
  patientProfileSelector,
  (profile) => Boolean(profile.referralSource)
)

export const patientFacilityUuidSelector = createSelector(
  patientProfileSelector,
  (profile) => profile.facility?.uuid ?? null
)

export const patientPrimaryDiseaseSelector = createSelector(
  patientProfileSelector,
  (profile) => profile.diseases.find((el) => el.type === 1) || {}
)

export const patientEnrolledProgramStatusSelector = createSelector(
  patientProfileSelector,
  (profile) => profile.enrolledProgramStatus
)

export const patientChatMessages$ = createSelector(
  patientProfileSelector,
  (profile) => profile.chatMessages
)

export const patientFirstName$ = createSelector(
  patientProfileSelector,
  (profile) => profile.firstName
)

export const patientLastName$ = createSelector(
  patientProfileSelector,
  (profile) => profile.lastName
)

export const patientFullName$ = createSelector(
  patientProfileSelector,
  (profile) => profile.fullName
)

export const patientReadingAnswers$ = createSelector(
  patientProfileSelector,
  (profile) => profile.readingAnswers
)

export const patientDocument$ = createSelector(
  patientProfileSelector,
  (profile) => profile.document
)

export const patientConsentDate$ = createSelector(
  patientProfileSelector,
  (profile) => profile.consentDate
)

export const patientPrimaryPhone$ = createSelector(
  patientProfileSelector,
  (profile) => profile.phone
)

export const patientMedicalDevice$ = createSelector(
  patientProfileSelector,
  (profile = {}) => get(profile, ['medicalDevice'], [])
)

export const patientIsDashboard$ = createSelector(
  patientProfileSelector,
  ({ isDashboard }) => isDashboard
)

export const patientPrimaryLanguage$ = createSelector(
  patientProfileSelector,
  ({ primaryLanguage }) => primaryLanguage
)

export const patientCompanyDivisionUuid$ = createSelector(
  patientProfileSelector,
  (profile) => profile?.companyDivision?.uuid || null
)

export const patientPrimaryDisease$ = createSelector(
  patientProgramSelector,
  patientProfileSelector,
  ({ patientDiseases }, { diseases }) => {
    const programDisease = lodashFind(
      patientDiseases,
      ({ isPrimary }) => isPrimary
    )
    const profileDisease = lodashFind(diseases, ({ isPrimary }) => isPrimary)

    return profileDisease || programDisease
  }
)

export const notificationRecipient$ = createSelector(
  patientProfileSelector,
  ({ notificationRecipient }) => notificationRecipient
)

export const gamificationEnabled$ = createSelector(
  patientProfileSelector,
  ({ gamificationEnabled }) => gamificationEnabled
)

export const patientIsNextCallDateRequired$ = createSelector(
  patientProfileSelector,
  (profile = {}) => get(profile, ['isNextCallDateRequired'], false)
)

export const patientlastChartReviewDate$ = createSelector(
  patientProfileSelector,
  (profile = {}) => profile?.lastChartReviewDate
)

export const patientProvider$ = createSelector(
  patientProfileSelector,
  (profile = {}) => profile?.provider || {}
)

export const patientClinician$ = createSelector(
  patientProfileSelector,
  (profile = {}) => profile?.clinician || {}
)

export const patientClinicianFacilityInfo$ = createSelector(
  patientProfileSelector,
  (profile = {}) => ({
    facility: profile?.facility,
    floor: profile?.floor,
    room: profile?.room
  })
)

export const patientProfileNewAppointment$ = createSelector(
  patientClinician$,
  patientEnrolledProgramStatusSelector,
  patientFullName$,
  patientIdSelector,
  patientProvider$,
  patientUuidSelector,
  patientClinicianFacilityInfo$,
  patientlastChartReviewDate$,
  (
    clinician,
    enrolledProgramStatus,
    fullName,
    patientId,
    provider,
    uuid,
    facility,
    lastChartReviewDate
  ) => ({
    clinician,
    enrolledProgramStatus,
    fullName,
    patientId,
    provider,
    uuid,
    lastChartReviewDate,
    ...facility
  })
)

export const patientPayer$ = createSelector(
  patientProfileSelector,
  (profile = {}) => profile?.payer || null
)

export const patientIsDischargeAllowed$ = createSelector(
  patientProfileSelector,
  (profile = {}) => profile?.isDischargeAllowed || false
)

export const updatePatientLastTelemedicineCall = (date) => (dispatch) => {
  dispatch(setPatientLastTelemedicineCall(date))
}

export const patientWasEnrolledInCcm$ = createSelector(
  patientProfileSelector,
  (profile) => hasProgramType(profile?.programTypes, PROGRAMS.CCM)
)

export const patientWasEnrolledInRpm$ = createSelector(
  patientProfileSelector,
  (profile) => hasProgramType(profile?.programTypes, PROGRAMS.RPM)
)

export const patientWasEnrolledInRtm$ = createSelector(
  patientProfileSelector,
  (profile) => hasProgramType(profile?.programTypes, PROGRAMS.RTM)
)

export const patientCcmBillingProgram$ = createSelector(
  patientProfileSelector,
  (profile) => hasProgramType(profile?.programTypes, PROGRAMS.CCM)
)

export const patientRpmBillingProgram$ = createSelector(
  patientProfileSelector,
  (profile) => hasProgramType(profile?.programTypes, PROGRAMS.RPM)
)

export const patientRtmBillingProgram$ = createSelector(
  patientProfileSelector,
  (profile) => hasProgramType(profile?.programTypes, PROGRAMS.RTM)
)

export const patientBhiBillingProgram$ = createSelector(
  patientProfileSelector,
  (profile) => hasProgramType(profile?.programTypes, PROGRAMS.BHI)
)

// Used to gate automatic Activity Timer starts on patient page load — unlike
// the billing-program booleans above, this additionally requires
// `canStartTimerForAiSummary` so the timer isn't auto-started when the
// backend says it shouldn't be.
export const patientHasActiveBillableProgramForTimer$ = createSelector(
  patientProfileSelector,
  (profile) =>
    [PROGRAMS.RPM, PROGRAMS.CCM, PROGRAMS.RTM, PROGRAMS.BHI].some(
      (programType) => hasProgramType(profile?.programTypes, programType)
    ) && Boolean(profile?.canStartTimerForAiSummary)
)

// True when the patient is enrolled in one of the relevant billing programs
// but `canStartTimerForAiSummary` is false — i.e. the patient is enrolled,
// just not able to have the timer started. Used to show a warning instead of
// silently skipping the auto-timer.
export const patientHasPreActiveOnlyBillableProgramForTimer$ = createSelector(
  patientProfileSelector,
  (profile) => {
    const relevantProgramTypes = [
      PROGRAMS.RPM,
      PROGRAMS.CCM,
      PROGRAMS.RTM,
      PROGRAMS.BHI
    ]
    const isEnrolledInRelevantProgram = relevantProgramTypes.some(
      (programType) => hasProgramType(profile?.programTypes, programType)
    )

    return isEnrolledInRelevantProgram && !profile?.canStartTimerForAiSummary
  }
)

export const patientClinicianCarePlan$ = createSelector(
  patientProfileSelector,
  (profile = {}) => profile?.carePlanClinician || null
)

export const patientNextRpmStartDate$ = createSelector(
  patientProfileSelector,
  (profile = {}) => profile?.nextRpmStartDate
)

export const pccPatientId$ = createSelector(
  patientProfileSelector,
  (profile = {}) => profile?.pccPatientId
)

export const patientRiskRecalculatedTimestamp$ = createSelector(
  patientProfileSelector,
  (profile = {}) => profile?.riskRecalculatedTimestamp
)
